import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useDiary, pixelY, getViewportHeight, formatTime, BASE_PX_PER_MINUTE } from './DiaryContext';
import { useDrag } from './DragContext';
import StaffHeader from './StaffHeader';
import EventBlock from './EventBlock';
import NonWorkingOverlay from './NonWorkingOverlay';
import OtherSiteBlocks from './OtherSiteBlocks';
import SlotPopup from './SlotPopup';
import { computeOverlapLanes } from './overlapLayout';
import PlacementBlock from './repeat/PlacementBlock';
import { usePlacement, getPlacement, movePlacement, columnMounted } from './repeat/placement';
import { useDraftPreview } from './draftPreview';

// Default ghost block duration (30 min)
const GHOST_DURATION = 30;

/**
 * Snap a raw minute value to the nearest slot boundary.
 */
function snapToSlot(minutes, slotDuration) {
  return Math.floor(minutes / slotDuration) * slotDuration;
}

/**
 * Check whether a time range overlaps any event.
 */
function overlapsEvent(startMin, endMin, events) {
  return events.some(e => startMin < e.endMinutes && endMin > e.startMinutes);
}

/**
 * StaffColumn - One vertical column per staff member.
 * Contains the time grid background, non-working overlay, event blocks,
 * a hover ghost block, and an anchored action popup on click.
 */
function StaffColumn({ staff, events }) {
  const { state, onSlotClick, skipSlotPopup } = useDiary();
  const { viewStartMinutes, viewEndMinutes, zoomLevel, slotDurationMinutes } = state;
  const { dragState, registerColumn, lastDragEndRef, deactivateEvent } = useDrag();
  const [ghostMinutes, setGhostMinutes] = useState(null);
  const [activeSlot, setActiveSlot] = useState(null); // { minutes } when popup is open
  const ghostRef = useRef(null);
  const columnRef = useRef(null);
  // A repeat visit being placed by hand (Change time): it shows in its
  // stylist's column on its day, and taps move it rather than start a booking
  const placement = usePlacement();
  // A booking tucked away in the pill: hovering shows where it would go
  const draft = useDraftPreview();
  const [draftAt, setDraftAt] = useState(null);
  // Open booking drawn here: bring it into view when it lands or moves
  const pinnedRef = useRef(null);
  const pinnedKey = draft && draft.pinned && staff && String(draft.pinned.staffId) === String(staff.id) && draft.pinned.date === state.selectedDate
    ? `${draft.pinned.date}|${draft.pinned.minutes}` : null;
  useEffect(() => {
    if (pinnedKey && pinnedRef.current && pinnedRef.current.scrollIntoView) {
      pinnedRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }, [pinnedKey]);
  const placingHere = !!(placement && staff && String(placement.staffId) === String(staff.id) && placement.date === state.selectedDate);
  useEffect(() => columnMounted(), []);

  // Register column body element for drag hit-testing
  const columnBodyRef = useCallback((el) => {
    columnRef.current = el;
    if (staff) registerColumn(staff.id, el);
  }, [staff, registerColumn]);

  // Is a drag currently hovering over this column?
  const isDropTarget = dragState && dragState.mode === 'move' && dragState.currentStaffId === staff?.id;
  const dropMinutes = isDropTarget ? dragState.currentMinutes : null;

  // Is a resize happening on an event in this column?
  const isResizing = dragState && dragState.mode === 'resize' && dragState.originStaffId === staff?.id;
  const resizeEventId = isResizing ? dragState.event.id : null;
  const resizeStartMinutes = isResizing ? dragState.newStartMinutes : null;
  const resizeEndMinutes = isResizing ? dragState.newEndMinutes : null;

  const handleColumnClick = useCallback((e) => {
    // Placing a repeat visit: a tap in its column moves it there; taps
    // elsewhere do nothing (never start a new booking mid-placement)
    const placing = getPlacement();
    if (placing) {
      if (staff && String(placing.staffId) === String(staff.id) && placing.date === state.selectedDate) {
        const r = e.currentTarget.getBoundingClientRect();
        const m = viewStartMinutes + (e.clientY - r.top) / (BASE_PX_PER_MINUTE * zoomLevel);
        movePlacement(Math.max(viewStartMinutes, Math.round(m / 5) * 5));
      }
      return;
    }
    // Deactivate any activated event when tapping empty space
    if (deactivateEvent) deactivateEvent();
    if (!onSlotClick || !staff) return;
    // Ignore clicks that fire immediately after a drag/resize ends
    if (Date.now() - lastDragEndRef.current < 300) return;
    // Don't open popup if clicking on an event (stopPropagation handles this,
    // but guard just in case)
    if (e.target.closest('.dv2-event-block')) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const minutesFromViewStart = clickY / (BASE_PX_PER_MINUTE * zoomLevel);
    const totalMinutes = viewStartMinutes + minutesFromViewStart;
    const snapped = snapToSlot(totalMinutes, slotDurationMinutes);

    // On touch devices or when booking panel is minimised, skip popup
    const isTouch = window.matchMedia('(hover: none)').matches;
    if (isTouch || skipSlotPopup) {
      onSlotClick(staff, snapped, 'appointment');
      return;
    }

    setActiveSlot({ minutes: snapped });
    setGhostMinutes(null); // hide hover ghost, locked ghost takes over
  }, [onSlotClick, skipSlotPopup, staff, viewStartMinutes, zoomLevel, slotDurationMinutes, state.selectedDate]);

  const handlePopupAction = useCallback((actionType) => {
    if (!onSlotClick || !staff || !activeSlot) return;
    onSlotClick(staff, activeSlot.minutes, actionType);
    setActiveSlot(null);
  }, [onSlotClick, staff, activeSlot]);

  const handlePopupClose = useCallback(() => {
    setActiveSlot(null);
  }, []);

  // Ghost hover handlers (desktop only, hidden when popup is open)
  const handleMouseMove = useCallback((e) => {
    if (activeSlot) return; // don't show hover ghost while popup is open
    if (getPlacement()) { setGhostMinutes(null); return; }
    if (draft && draft.hover && skipSlotPopup) {
      const r = e.currentTarget.getBoundingClientRect();
      const m = viewStartMinutes + (e.clientY - r.top) / (BASE_PX_PER_MINUTE * zoomLevel);
      setDraftAt(snapToSlot(m, slotDurationMinutes));
      setGhostMinutes(null);
      return;
    }
    if (!onSlotClick) return; // read-only host: a "+" that does nothing is a lie
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const rawMinutes = viewStartMinutes + y / (BASE_PX_PER_MINUTE * zoomLevel);
    const snapped = snapToSlot(rawMinutes, slotDurationMinutes);

    if (overlapsEvent(snapped, snapped + GHOST_DURATION, events || [])) {
      setGhostMinutes(null);
    } else {
      setGhostMinutes(snapped);
    }
  }, [viewStartMinutes, zoomLevel, slotDurationMinutes, events, activeSlot, onSlotClick, draft, skipSlotPopup]);

  const handleMouseLeave = useCallback(() => {
    if (!activeSlot) setGhostMinutes(null);
    setDraftAt(null);
  }, [activeSlot]);

  // How much of the preview fits: short bookings drop to one line (time and
  // name), very short ones to the time alone, never clipped text
  const ghostFit = (duration) => {
    const h = pixelY(duration, 0, zoomLevel);
    return h < 30 ? ' is-tiny' : h < 64 ? ' is-short' : '';
  };

  const totalHeight = getViewportHeight(viewStartMinutes, viewEndMinutes, zoomLevel);

  // Grid line background
  const slotPx = pixelY(slotDurationMinutes, 0, zoomLevel);
  const hourPx = pixelY(60, 0, zoomLevel);

  const gridBackground = useMemo(() => {
    const minor = `repeating-linear-gradient(
      to bottom,
      transparent 0px,
      transparent ${slotPx - 1}px,
      #f5f5f7 ${slotPx - 1}px,
      #f5f5f7 ${slotPx}px
    )`;
    const major = `repeating-linear-gradient(
      to bottom,
      transparent 0px,
      transparent ${hourPx - 1}px,
      #ebebed ${hourPx - 1}px,
      #ebebed ${hourPx}px
    )`;
    return `${major}, ${minor}`;
  }, [slotPx, hourPx]);

  // Compute overlap lanes
  const laidOutEvents = useMemo(() => {
    return computeOverlapLanes(events || []);
  }, [events]);

  // Ghost block position (hover or locked)
  const displayMinutes = activeSlot ? activeSlot.minutes : ghostMinutes;
  const ghostTop = displayMinutes !== null ? pixelY(displayMinutes, viewStartMinutes, zoomLevel) : 0;
  const ghostHeight = pixelY(GHOST_DURATION, 0, zoomLevel);
  const isLocked = !!activeSlot;

  return (
    <div className={`dv2-staff-column${staff.raw && staff.raw.isOnboardingDemo ? ' dv2-staff-column--demo' : ''}`} data-staff-id={staff.id}>
      <StaffHeader staff={staff} />
      <div
        ref={columnBodyRef}
        className={`dv2-column-body ${isDropTarget ? 'dv2-column-body--drop-target' : ''}`}
        style={{
          height: totalHeight,
          backgroundImage: gridBackground,
          backgroundSize: `100% ${slotPx}px, 100% ${hourPx}px`,
          padding: '0 4px',
        }}
        onClick={handleColumnClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <NonWorkingOverlay staff={staff} />
        <OtherSiteBlocks staff={staff} />
        {laidOutEvents.map(evt => (
          <EventBlock
            key={evt.id}
            event={evt}
            lane={evt.lane}
            laneCount={evt.laneCount}
            overflow={evt.overflow || 0}
            hidden={evt.hidden || false}
            resizeStartMinutes={evt.id === resizeEventId ? resizeStartMinutes : null}
            resizeEndMinutes={evt.id === resizeEventId ? resizeEndMinutes : null}
          />
        ))}
        {/* Drop indicator: shows where the event will land */}
        {dropMinutes !== null && dragState && (
          <div
            className="dv2-drop-indicator"
            style={{
              top: pixelY(dropMinutes, viewStartMinutes, zoomLevel),
              height: pixelY(dragState.event.endMinutes - dragState.event.startMinutes, 0, zoomLevel),
              color: dragState.event.diaryColour || '#D6D5EC',
              background: dragState.event.diaryColour || '#D6D5EC',
              opacity: 0.85,
            }}
          >
            <span className="dv2-drop-indicator-time">{formatTime(dropMinutes)}</span>
          </div>
        )}
        {/* The booking being made, in its slot behind the open panel */}
        {draft && draft.pinned && !placement && staff && String(draft.pinned.staffId) === String(staff.id) && draft.pinned.date === state.selectedDate && (() => {
          const clash = overlapsEvent(draft.pinned.minutes, draft.pinned.minutes + draft.duration, events || []);
          return (
            <div
              ref={pinnedRef}
              className={`dv2-draft-ghost is-pinned${clash ? ' is-clash' : ''}${ghostFit(draft.duration)}`}
              style={{ top: pixelY(draft.pinned.minutes, viewStartMinutes, zoomLevel), height: pixelY(draft.duration, 0, zoomLevel) }}
              aria-hidden="true"
            >
              <span className="dv2-draft-ghost-time">{formatTime(draft.pinned.minutes)} to {formatTime(draft.pinned.minutes + draft.duration)}<span className="dv2-draft-ghost-inline"> · {draft.label}</span></span>
              <span className="dv2-draft-ghost-label">{draft.label}</span>
              {clash && <span className="dv2-draft-ghost-hint">Overlaps a booking</span>}
            </div>
          );
        })()}
        {draft && draft.hover && skipSlotPopup && draftAt !== null && !placement && !dragState && (() => {
          const clash = overlapsEvent(draftAt, draftAt + draft.duration, events || []);
          return (
            <div
              className={`dv2-draft-ghost${clash ? ' is-clash' : ''}${ghostFit(draft.duration)}`}
              style={{ top: pixelY(draftAt, viewStartMinutes, zoomLevel), height: pixelY(draft.duration, 0, zoomLevel) }}
              aria-hidden="true"
            >
              <span className="dv2-draft-ghost-time">{formatTime(draftAt)} to {formatTime(draftAt + draft.duration)}<span className="dv2-draft-ghost-inline"> · {draft.label}</span></span>
              <span className="dv2-draft-ghost-label">{draft.label}</span>
              <span className="dv2-draft-ghost-hint">{clash ? 'Overlaps a booking · tap to move here' : 'Tap to move here'}</span>
            </div>
          );
        })()}
        {placingHere && (
          <PlacementBlock
            placement={placement}
            events={events}
            viewStartMinutes={viewStartMinutes}
            viewEndMinutes={viewEndMinutes}
            zoomLevel={zoomLevel}
          />
        )}
        {displayMinutes !== null && !dragState && !placement && (
          <div
            ref={ghostRef}
            className={`dv2-ghost-block ${isLocked ? 'dv2-ghost-block--locked' : ''}`}
            style={{ top: ghostTop, height: ghostHeight }}
          >
            <span className="dv2-ghost-time">{formatTime(displayMinutes)}</span>
            <svg className="dv2-ghost-plus" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        )}
      </div>
      {activeSlot && (
        <SlotPopup
          ghostRef={ghostRef}
          onAction={handlePopupAction}
          onClose={handlePopupClose}
        />
      )}
    </div>
  );
}

export default React.memo(StaffColumn);
